import { useState, useRef, useEffect, useCallback } from "react";
import { Send, PanelLeft, Zap, Mic, Square, Download, Loader2, Volume2, VolumeX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_URL = "/edgar-avatar-small.jpg";

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 px-4 sm:px-8 lg:px-16 py-2">
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-emerald-500/30">
        <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
      </div>
      <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/50 rounded-2xl rounded-tl-sm px-5 py-4">
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-emerald-400 rounded-full"
              style={{ animation: "typing-dot 1.4s infinite", animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MarkdownContent({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="text-zinc-50 font-semibold">{children}</strong>,
        em: ({ children }) => <em className="text-zinc-200 italic">{children}</em>,
        ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="text-zinc-300">{children}</li>,
        code: ({ inline, children }) =>
          inline ? (
            <code className="bg-zinc-800/60 px-1.5 py-0.5 rounded text-emerald-300 text-[0.85em]">{children}</code>
          ) : (
            <pre className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-3 my-2 overflow-x-auto">
              <code className="text-xs text-zinc-300">{children}</code>
            </pre>
          ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-emerald-500/50 pl-3 my-2 text-zinc-400 italic">{children}</blockquote>
        ),
        h1: ({ children }) => <h1 className="text-lg font-bold text-zinc-50 mb-2">{children}</h1>,
        h2: ({ children }) => <h2 className="text-base font-bold text-zinc-50 mb-1.5">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-bold text-zinc-100 mb-1">{children}</h3>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="text-xs border border-zinc-800 rounded">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="px-3 py-1.5 bg-zinc-800 text-zinc-300 text-left font-medium border-b border-zinc-700">{children}</th>,
        td: ({ children }) => <td className="px-3 py-1.5 border-b border-zinc-800/50 text-zinc-400">{children}</td>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function PlayVoiceButton({ text }) {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const handlePlay = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      return;
    }

    // iOS Safari requires Audio to be created and played within user gesture
    // Create audio element immediately on tap and "unlock" it with silent play
    const audio = new Audio();
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => { setPlaying(false); setLoading(false); };

    setLoading(true);
    try {
      const res = await axios.post(`${API}/tts/generate`, { text }, { responseType: "json" });
      if (res.data.audio) {
        // Convert base64 to blob for better iOS compatibility
        const b64 = res.data.audio.split(",")[1];
        const binary = atob(b64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        audio.src = url;
        audio.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
        await audio.play();
        setPlaying(true);
      }
    } catch (e) {
      if (e.response?.status !== 503) console.error("TTS error", e);
    }
    setLoading(false);
  };

  return (
    <button
      data-testid="play-voice-btn"
      onClick={handlePlay}
      disabled={loading}
      className={`mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${
        playing
          ? "bg-emerald-500/20 text-emerald-400"
          : "text-zinc-500 hover:text-emerald-400 hover:bg-emerald-500/10"
      } disabled:opacity-40`}
      style={{ border: "1px solid var(--border-subtle)" }}
    >
      {loading ? (
        <Loader2 size={12} className="animate-spin" />
      ) : playing ? (
        <VolumeX size={12} />
      ) : (
        <Volume2 size={12} />
      )}
      {loading ? "Generando..." : playing ? "Detener" : "Escuchar"}
    </button>
  );
}

function ChatBubble({ message }) {
  const isUser = message.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`flex items-start gap-3 px-4 sm:px-8 lg:px-16 py-1.5 ${isUser ? "flex-row-reverse" : ""}`}
    >
      {!isUser && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-emerald-500/30 mt-1">
          <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
        </div>
      )}
      <div
        data-testid={isUser ? "user-message" : "assistant-message"}
        className={
          isUser
            ? "rounded-2xl rounded-tr-sm px-5 py-3.5 max-w-[75%] shadow-lg"
            : "backdrop-blur-xl rounded-2xl rounded-tl-sm px-5 py-3.5 max-w-[75%] shadow-lg"
        }
        style={{
          backgroundColor: isUser ? "var(--bg-bubble-user)" : "var(--bg-bubble-ai)",
          color: "var(--text-primary)",
          border: isUser ? "none" : "1px solid var(--border-subtle)",
        }}
      >
        {isUser ? (
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</div>
        ) : (
          <>
            <div className="markdown-content text-sm leading-relaxed">
              <MarkdownContent content={message.content} />
            </div>
            <PlayVoiceButton text={message.content} />
          </>
        )}
      </div>
    </motion.div>
  );
}

function EmptyState({ onSuggestion }) {
  const suggestions = [
    "¿Cómo empiezo como agente de fútbol?",
    "¿Cómo detecto talento real?",
    "¿Cómo negocio con un club?",
    "¿Qué errores cometen los agentes novatos?",
  ];

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="text-center max-w-lg"
      >
        <div className="w-20 h-20 mx-auto rounded-full overflow-hidden ring-4 ring-emerald-500/20 mb-6 shadow-[0_0_40px_rgba(16,185,129,0.15)]"
          style={{ animation: "pulse-glow 3s ease-in-out infinite" }}>
          <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
        </div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif", color: "var(--text-primary)" }}
        >
          Hola, soy Edgar IA
        </h2>
        <p className="text-sm mb-8 leading-relaxed" style={{ color: "var(--text-muted)" }}>
          Tu tutor personal de Agente 360. Pregúntame sobre fichajes, negociación con clubes,
          detección de talento o cualquier tema del mundo del fútbol profesional.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {suggestions.map((s, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, duration: 0.3 }}
              data-testid={`suggestion-${i}`}
              onClick={() => onSuggestion(s)}
              className="text-left px-4 py-3 rounded-xl text-sm transition-all group"
              style={{
                backgroundColor: "var(--bg-glass)",
                border: "1px solid var(--border-subtle)",
                color: "var(--text-secondary)",
              }}
            >
              <Zap size={12} className="inline mr-2 text-emerald-500 group-hover:text-emerald-400" />
              {s}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// Voice recording hook using MediaRecorder + Whisper API
function useVoiceRecorder(onTranscription) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const supported = typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getUserMedia;

  const start = useCallback(async () => {
    if (!supported || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { setRecording(false); return; } // Too short

        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append("file", blob, "voice.webm");
          const res = await axios.post(`${API}/chat/voice`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (res.data.text) onTranscription(res.data.text);
        } catch (e) {
          console.error("Transcription error", e);
        }
        setTranscribing(false);
        setRecording(false);
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (e) {
      console.error("Mic access error", e);
    }
  }, [supported, recording, onTranscription]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  return { recording, transcribing, start, stop, supported };
}

export default function ChatView({ messages, onSend, loading, activeSessionId, onToggleSidebar, sidebarOpen }) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const handleVoiceResult = useCallback((transcript) => {
    // Auto-send voice message directly with fromVoice flag
    if (transcript.trim()) {
      onSend(transcript, true);
    }
  }, [onSend]);

  const { recording, transcribing, start: startRecording, stop: stopRecording, supported: voiceSupported } = useVoiceRecorder(handleVoiceResult);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    onSend(input);
    setInput("");
  };

  const handleExportPdf = async () => {
    if (!activeSessionId) return;
    try {
      const res = await axios.get(`${API}/export/chat/${activeSessionId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `chat_${activeSessionId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error exporting PDF", e);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0 backdrop-blur-sm" style={{ borderBottom: "1px solid var(--border-main)", backgroundColor: "var(--bg-glass)" }}>
        {!sidebarOpen && (
          <button data-testid="open-sidebar-btn" onClick={onToggleSidebar}
            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors">
            <PanelLeft size={18} />
          </button>
        )}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-emerald-500/30">
            <img src={AVATAR_URL} alt="Edgar IA" className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif", color: "var(--text-primary)" }}>
              Edgar IA
            </h3>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>Tutor Agente 360</span>
            </div>
          </div>
        </div>
        {activeSessionId && messages.length > 0 && (
          <button
            data-testid="export-chat-pdf-btn"
            onClick={handleExportPdf}
            className="ml-auto p-2 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-emerald-400 transition-colors"
            title="Exportar como PDF"
          >
            <Download size={16} />
          </button>
        )}
      </div>

      {/* Messages */}
      {messages.length === 0 && !loading ? (
        <EmptyState onSuggestion={(text) => onSend(text)} />
      ) : (
        <div className="flex-1 overflow-y-auto py-4 space-y-1 min-h-0">
          <AnimatePresence>
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
          </AnimatePresence>
          {loading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <div className="p-3 sm:p-4 flex-shrink-0">
        <form
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto backdrop-blur-xl p-1.5 rounded-full flex items-center focus-within:border-zinc-700 transition-colors"
          style={{ backgroundColor: "var(--bg-glass)", border: "1px solid var(--border-main)", boxShadow: "var(--card-shadow)" }}
        >
          <input
            ref={inputRef}
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={recording ? "Grabando..." : transcribing ? "Transcribiendo..." : "Preguntale a Edgar..."}
            className="flex-1 bg-transparent placeholder-zinc-500 px-4 py-2.5 text-sm outline-none min-w-0"
            style={{ color: "var(--text-primary)" }}
            disabled={loading}
          />
          {voiceSupported && (
            <button
              data-testid="voice-input-btn"
              type="button"
              onClick={recording ? stopRecording : startRecording}
              disabled={loading || transcribing}
              className={`p-2 sm:p-2.5 rounded-full transition-all mr-0.5 flex-shrink-0 ${
                recording
                  ? "bg-red-500/20 text-red-400 animate-pulse"
                  : transcribing
                  ? "text-amber-400"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
              } disabled:opacity-30`}
              title={recording ? "Detener grabacion" : "Enviar mensaje de voz"}
            >
              {recording ? <Square size={18} /> : transcribing ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
            </button>
          )}
          <button
            data-testid="send-message-btn"
            type="submit"
            disabled={!input.trim() || loading}
            className="bg-emerald-500 text-zinc-950 rounded-full p-3 sm:p-2.5 transition-all hover:bg-emerald-400 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95 shadow-[0_0_16px_rgba(16,185,129,0.2)] flex-shrink-0"
          >
            <Send size={20} className="sm:w-4 sm:h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
